// TiptapEditor.js
//
// PURPOSE:
// Modern WYSIWYG rich text editor component using Tiptap (React 19 compatible).
// Provides text formatting, image/video embedding with secure upload to server.
//
// SECURITY FEATURES:
// - Image uploads validated on client and server
// - File type and size restrictions enforced
// - Authentication required for uploads (idToken)
// - XSS prevention via Tiptap's built-in sanitization
// - No direct HTML injection - uses structured content model
//
// FEATURES:
// - Rich text formatting (bold, italic, underline, strike)
// - Headings (H1, H2, H3)
// - Lists (ordered, unordered)
// - Links with validation
// - Image upload with server storage
// - YouTube video embedding
// - Clean, production-ready UI
//
// USAGE:
// <TiptapEditor
//   value={content}
//   onChange={(html) => setContent(html)}
//   onImageUpload={async (file) => uploadToServer(file)}
//   placeholder="Start writing..."
// />

import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import './TiptapEditor.css';

/**
 * TiptapEditor Component
 * 
 * @param {string} value - HTML content to display in editor
 * @param {function} onChange - Callback when content changes, receives HTML string
 * @param {function} onImageUpload - Async function to upload image, returns URL
 * @param {string} placeholder - Placeholder text when editor is empty
 * @param {boolean} disabled - Disable editing (read-only mode)
 */
const TiptapEditor = ({ 
  value = '', 
  onChange, 
  onImageUpload,
  placeholder = 'Commencez à écrire...',
  disabled = false
}) => {
  // Initialize Tiptap editor with extensions
  const editor = useEditor({
    extensions: [
      // StarterKit includes: Bold, Italic, Strike, Code, Paragraph, Heading, Lists, etc.
      // IMPORTANT: StarterKit already includes Link, so we extend it instead of adding separately
      StarterKit.extend({
        link: false, // Disable default link to use our custom config
      }).configure({
        heading: {
          levels: [1, 2, 3], // Only H1, H2, H3
        },
      }),
      // Image extension for embedded images
      Image.configure({
        inline: true,
        allowBase64: false, // SECURITY: Prevent base64 to avoid large payload attacks
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      // YouTube extension for video embedding
      Youtube.configure({
        controls: true,
        nocookie: true, // Privacy-enhanced mode
        HTMLAttributes: {
          class: 'tiptap-youtube',
        },
      }),
      // Link extension with validation (replaces default StarterKit link)
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
          rel: 'noopener noreferrer', // SECURITY: Prevent tabnabbing
          target: '_blank',
        },
        validate: href => /^https?:\/\//.test(href), // SECURITY: Only http/https links
      }),
      // Placeholder extension
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    // Emit HTML to parent component on every change
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  // Sync external value changes to editor
  // Prevents cursor jumping during typing
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    
    const currentContent = editor.getHTML();
    if (currentContent !== value && value !== undefined) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  // Handle image upload with validation
  const handleImageUpload = useCallback(async () => {
    if (!onImageUpload) {
      alert('Upload d\'images non configuré');
      return;
    }

    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
    input.click();

    input.onchange = async (e) => {
      const file = e.target?.files?.[0];
      if (!file) return;

      // SECURITY: Validate file size (5MB max)
      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        alert('Fichier trop volumineux. Taille maximale : 5MB');
        return;
      }

      // SECURITY: Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Type de fichier non autorisé. Utilisez JPEG, PNG ou WebP.');
        return;
      }

      try {
        // Upload to server and get URL
        const url = await onImageUpload(file);
        
        if (url && editor) {
          // Insert image at current cursor position
          editor.chain().focus().setImage({ src: url }).run();
        } else {
          alert('Erreur lors de l\'upload de l\'image');
        }
      } catch (error) {
        alert(`Erreur lors de l'upload: ${error.message}`);
      }
    };
  }, [editor, onImageUpload]);

  // Handle YouTube video embedding
  const handleAddYouTube = useCallback(() => {
    const url = prompt('Entrez l\'URL YouTube:');
    
    if (!url) return;

    // SECURITY: Validate YouTube URL format
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(url)) {
      alert('URL YouTube invalide');
      return;
    }

    if (editor) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run();
    }
  }, [editor]);

  // Handle link insertion
  const handleAddLink = useCallback(() => {
    const url = prompt('Entrez l\'URL du lien:');
    
    if (!url) return;

    // SECURITY: Validate URL format (http/https only)
    if (!/^https?:\/\//.test(url)) {
      alert('URL invalide. Utilisez http:// ou https://');
      return;
    }

    if (editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  // Return null if editor not initialized
  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-editor-wrapper">
      {/* Toolbar */}
      <div className="tiptap-toolbar">
        {/* Text formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'is-active' : ''}
          title="Gras"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'is-active' : ''}
          title="Italique"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive('strike') ? 'is-active' : ''}
          title="Barré"
        >
          <strike>S</strike>
        </button>

        <div className="toolbar-separator" />

        {/* Headings */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
          title="Titre 1"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
          title="Titre 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
          title="Titre 3"
        >
          H3
        </button>

        <div className="toolbar-separator" />

        {/* Lists */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'is-active' : ''}
          title="Liste à puces"
        >
          • Liste
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'is-active' : ''}
          title="Liste numérotée"
        >
          1. Liste
        </button>

        <div className="toolbar-separator" />

        {/* Media & Links */}
        <button
          type="button"
          onClick={handleAddLink}
          className={editor.isActive('link') ? 'is-active' : ''}
          title="Insérer un lien"
        >
          🔗 Lien
        </button>
        <button
          type="button"
          onClick={handleImageUpload}
          title="Insérer une image"
        >
          🖼️ Image
        </button>
        <button
          type="button"
          onClick={handleAddYouTube}
          title="Insérer une vidéo YouTube"
        >
          📹 Vidéo
        </button>

        <div className="toolbar-separator" />

        {/* Clear formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Effacer le formatage"
        >
          🧹 Effacer
        </button>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} className="tiptap-content" />
    </div>
  );
};

export default TiptapEditor;
